/**
 * Admin — Settings/Integrations (W1b-07, wired to backend)
 *
 * Lists tenant integrations grouped by type. Real CRUD via
 * /api/v1/admin/settings/integrations:
 *   - test-fire button hits POST /:id/test (5s probe)
 *   - active/inactive toggle hits PATCH /:id with {enabled}
 *   - delete via DELETE /:id with TypedConfirmDialog
 *   - ITAR-clear via POST /:id/itar-clear (privileged + typed-confirm)
 *
 * The UI has been ported from the prior mock-data implementation; visual
 * structure is preserved (SectionCard groupings, status dot, latency probe
 * dialog) but every state mutation flows through TanStack Query.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Plug, Plus, TestTube2, Trash2, Pencil, MoreHorizontal,
  Database as DatabaseIcon, Mail, MessageSquare, Users as UsersIcon,
  HardDrive, Calendar, Shield, CheckCircle2, XCircle,
  AlertTriangle, Clock, Lock, ShieldCheck,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { SectionCard, StatusDot } from '@/components/admin';
import { TypedConfirmDialog } from '@/components/admin/typed-confirm-dialog';
import { apiClient } from '@/lib/api-client';
import { queryClient } from '@/lib/query-client';
import {
  isRelayNotConfigured,
  parsePortInput,
} from './outgoing-mail.helpers';

export const Route = createFileRoute('/admin/settings/integrations')({
  component: IntegrationsPage,
});

// ─── Types matching the backend response ─────────────────────────────────────

/**
 * SMTP IS NOT ONE OF THEM (Foundations 2 / B2d-T3). The tenant's outgoing mail
 * relay is its own noun with its own owner — `/admin/settings/email` — and it
 * renders in the "Outgoing mail" section at the top of this page rather than as
 * a row in the generic list. The API no longer accepts, lists or serves the
 * type here, so keeping it in this union would let the page ask for something
 * that cannot come back.
 */
type IntegrationType =
  | 'siem'
  | 'chat_ops'
  | 'scim'
  | 'object_storage'
  | 'calendar'
  | 's3_object_lock'
  | 'azure_immutable_blob';

type IntegrationStatus = 'disabled' | 'healthy' | 'degraded' | 'failed';

interface Integration {
  id: string;
  type: IntegrationType;
  name: string;
  provider: string | null;
  endpoint: string | null;
  enabled: boolean;
  status: IntegrationStatus;
  config: Record<string, unknown>;
  itarCleared: boolean;
  credentialsVaultRef: string | null;
  lastTestAt: string | null;
  lastTestResult: {
    success: boolean;
    latencyMs: number | null;
    errorMessage: string | null;
    errorCode: string | null;
  } | null;
  createdAt: string;
  changedAt: string;
}

interface ListResponse {
  items: Integration[];
  total: number;
}

const TYPE_META: Record<IntegrationType, { label: string; description: string }> = {
  siem: { label: 'SIEM / Log forwarding', description: 'Forward audit + security logs to Splunk / Datadog / Elastic.' },
  chat_ops: { label: 'Chat Ops', description: 'Slack / Teams / Mattermost webhooks + bot commands.' },
  scim: { label: 'SCIM provisioning', description: 'Inbound user sync from Okta / Azure AD / OneLogin.' },
  object_storage: { label: 'Object Storage', description: 'S3 / GCS / Azure Blob / MinIO for backups + exports.' },
  calendar: { label: 'Calendar', description: 'Google / Outlook / CalDAV for scheduled-agent triggers.' },
  s3_object_lock: { label: 'S3 Object Lock (WORM)', description: 'Write-once destination for audit witness rows.' },
  azure_immutable_blob: { label: 'Azure Immutable Blob', description: 'WORM storage on Azure for audit witness rows.' },
};

const TYPE_ICON: Record<IntegrationType, React.ComponentType<{ className?: string }>> = {
  siem: DatabaseIcon,
  chat_ops: MessageSquare,
  scim: UsersIcon,
  object_storage: HardDrive,
  calendar: Calendar,
  s3_object_lock: HardDrive,
  azure_immutable_blob: HardDrive,
};

const TYPE_COLOR: Record<IntegrationType, string> = {
  siem: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  chat_ops: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  scim: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  object_storage: 'bg-slate-500/10 text-slate-700 dark:text-slate-300',
  calendar: 'bg-pink-500/10 text-pink-600 dark:text-pink-400',
  s3_object_lock: 'bg-stone-500/10 text-stone-700 dark:text-stone-300',
  azure_immutable_blob: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
};

// ─── Page ─────────────────────────────────────────────────────────────────

function IntegrationsPage() {
  const [editing, setEditing] = useState<Integration | null>(null);
  const [testing, setTesting] = useState<Integration | null>(null);
  const [deleting, setDeleting] = useState<Integration | null>(null);
  const [creating, setCreating] = useState(false);
  const [itarTarget, setItarTarget] = useState<Integration | null>(null);

  const listQuery = useQuery<ListResponse>({
    queryKey: ['admin', 'settings', 'integrations'],
    queryFn: () =>
      apiClient.get<ListResponse>('/admin/settings/integrations?limit=200'),
  });

  const items = listQuery.data?.items ?? [];

  const grouped = useMemo(() => {
    const out: Record<IntegrationType, Integration[]> = {
      siem: [], chat_ops: [], scim: [], object_storage: [],
      calendar: [], s3_object_lock: [], azure_immutable_blob: [],
    };
    for (const i of items) out[i.type].push(i);
    return out;
  }, [items]);

  const healthy = items.filter(i => i.status === 'healthy' && i.enabled).length;
  const degraded = items.filter(i => i.status === 'degraded' && i.enabled).length;
  const failed = items.filter(i => i.status === 'failed' && i.enabled).length;

  const toggleMutation = useMutation({
    mutationFn: (params: { id: string; enabled: boolean }) =>
      apiClient.patch(`/admin/settings/integrations/${params.id}`, {
        enabled: params.enabled,
      }),
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'settings', 'integrations'],
      });
      toast.success(vars.enabled ? 'Integration enabled' : 'Integration disabled');
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Toggle failed';
      toast.error(`Toggle failed: ${m}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/admin/settings/integrations/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'settings', 'integrations'],
      });
      toast.success('Integration removed');
      setDeleting(null);
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Delete failed';
      toast.error(`Delete failed: ${m}`);
    },
  });

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5" data-testid="settings-integrations">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Integrations</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Outbound connectors for log forwarding, email, chat ops, user provisioning, storage, and calendars.
          </p>
        </div>
        <Button
          size="sm"
          className="h-8 text-xs gap-1.5"
          onClick={() => setCreating(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          Add Integration
        </Button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Healthy" value={healthy} accent="emerald" icon={CheckCircle2} />
        <StatCard label="Degraded" value={degraded} accent="amber" icon={AlertTriangle} />
        <StatCard label="Failed" value={failed} accent="red" icon={XCircle} />
        <StatCard label="Total" value={items.length} accent="slate" icon={Plug} />
      </div>

      {/* Outgoing mail — its own noun, its own owner (B2d-T3). */}
      <OutgoingMailSection />

      {listQuery.isLoading && (
        <div className="text-xs text-muted-foreground">Loading integrations…</div>
      )}

      {listQuery.error && (
        <div className="text-xs text-red-600 dark:text-red-400">
          Failed to load integrations: {listQuery.error instanceof Error ? listQuery.error.message : 'unknown error'}
        </div>
      )}

      {/* Grouped by type */}
      {(Object.keys(grouped) as IntegrationType[]).map(type => {
        const list = grouped[type];
        if (list.length === 0) return null;
        const meta = TYPE_META[type];
        const Icon = TYPE_ICON[type];
        return (
          <SectionCard
            key={type}
            title={meta.label}
            description={meta.description}
            icon={Icon}
            actions={
              <Badge variant="secondary" className="h-5 text-[10px] px-1.5 tabular-nums">
                {list.length}
              </Badge>
            }
          >
            <div className="space-y-2">
              {list.map(i => (
                <IntegrationRow
                  key={i.id}
                  integration={i}
                  onToggle={(e) => toggleMutation.mutate({ id: i.id, enabled: e })}
                  onEdit={() => setEditing(i)}
                  onTest={() => setTesting(i)}
                  onDelete={() => setDeleting(i)}
                  onItar={() => setItarTarget(i)}
                />
              ))}
            </div>
          </SectionCard>
        );
      })}

      {items.length === 0 && !listQuery.isLoading && (
        <SectionCard
          title="No integrations yet"
          description="Add your first outbound connector to start delivering logs, sending email, or syncing users."
        >
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(TYPE_META) as IntegrationType[]).map(type => {
              const meta = TYPE_META[type];
              const Icon = TYPE_ICON[type];
              return (
                <button
                  key={type}
                  onClick={() => setCreating(true)}
                  className="flex items-start gap-2.5 p-3 border rounded-md hover:bg-muted/30 text-left transition-colors"
                >
                  <span className={cn('h-8 w-8 rounded flex items-center justify-center shrink-0', TYPE_COLOR[type])}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs font-medium">{meta.label}</div>
                    <div className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">
                      {meta.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Dialogs / sheets */}
      {editing && (
        <IntegrationEditorSheet
          integration={editing}
          open
          onClose={() => setEditing(null)}
        />
      )}
      {testing && (
        <TestFireDialog
          integration={testing}
          open
          onClose={() => setTesting(null)}
        />
      )}
      {deleting && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setDeleting(null)}
          title="Remove integration"
          description={`Remove ${deleting.name}? Outbound events targeting this endpoint will stop immediately. Existing logs are retained.`}
          consequences={[
            `${deleting.name} will stop receiving outbound events`,
            'Queued deliveries will be dropped',
            'Logs of past deliveries are retained for audit',
          ]}
          confirmPhrase={deleting.name}
          confirmLabel="Remove integration"
          variant="danger"
          onConfirm={() => deleteMutation.mutate(deleting.id)}
        />
      )}
      {creating && (
        <CreateIntegrationDialog
          open
          onClose={() => setCreating(false)}
        />
      )}
      {itarTarget && (
        <ItarClearDialog
          integration={itarTarget}
          open
          onClose={() => setItarTarget(null)}
        />
      )}
    </div>
  );
}

// ─── Outgoing mail (B2d-T3) ─────────────────────────────────────────────────
//
// THE TENANT'S OWN RELAY, and the record of what it sent.
//
// decisions.md 2026-09-08 (Users rail) #2: "Email is a tenant SMTP setting,
// configured in Admin, as ThingsBoard does." It used to be a row in the generic
// list above, and it could not work there: the credential went into a `vault://`
// pointer nobody had built a vault for, the `secure` field was a boolean the
// mail transport could not read, and "Test connection" fired an HTTPS HEAD at
// the SMTP host — a probe that says nothing about whether mail can be sent.
//
// So it is its own section, served by `/admin/settings/email`, and "Send test
// message" goes through the SAME send path as an invitation and shows the
// delivery row it actually wrote.
//
// THE PASSWORD IS NEVER SERVED. The read shape carries `hasCredentials` and no
// password field at all, so the input below is blank on load and an empty
// input on save means "leave the stored credential alone" — not "clear it".
// Clearing is its own control, because those are different acts.

interface TenantMailSetting {
  host: string;
  port: number;
  secure: 'tls' | 'starttls' | 'none';
  username: string | null;
  hasCredentials: boolean;
  fromAddress: string;
  fromName: string | null;
  replyTo: string | null;
  enabled: boolean;
  updatedAt: string;
}

interface MailDeliveryRow {
  id: string;
  category: string;
  toAddress: string;
  subject: string;
  transport: 'tenant' | 'platform' | 'none';
  outcome: 'sent' | 'failed' | 'disabled';
  errorCode: string | null;
  error: string | null;
  messageId: string | null;
  createdAt: string;
}

interface MailTestResult {
  delivery: 'sent' | 'failed' | 'disabled';
  transport: 'tenant' | 'platform' | 'none';
  errorCode: string | null;
  error: string | null;
  deliveryRow: MailDeliveryRow | null;
}

const EMPTY_MAIL_FORM = {
  host: '',
  // A STRING, because that is what an input holds while it is being typed
  // (B2dT3-10). It used to be coerced with `Number(e.target.value)` on every
  // keystroke, so backspacing the field to retype it left `0` in the form and
  // a half-typed value left `NaN` — both of which serialized into the payload
  // and came back as a raw Zod error for a field the admin was still editing.
  port: '587',
  secure: 'starttls' as TenantMailSetting['secure'],
  username: '',
  password: '',
  fromAddress: '',
  fromName: '',
  replyTo: '',
  enabled: true,
};

function OutgoingMailSection() {
  const [form, setForm] = useState({ ...EMPTY_MAIL_FORM });
  const [loaded, setLoaded] = useState(false);
  const [lastTest, setLastTest] = useState<MailTestResult | null>(null);

  const settingQuery = useQuery<TenantMailSetting | null>({
    queryKey: ['admin', 'settings', 'email'],
    queryFn: async () => {
      try {
        return await apiClient.get<TenantMailSetting>('/admin/settings/email');
      } catch (err) {
        // 404 — no relay configured. That is a state, not an error: mail goes
        // out over the platform default until somebody configures one here.
        if (isRelayNotConfigured(err)) return null;
        // ANYTHING ELSE IS AN ERROR AND IS SHOWN AS ONE (R5). This used to
        // swallow every failure and return null, so a 500 or a 403 rendered the
        // same blank form as "no relay configured" — and saving that form
        // replaced the stored host, username, from name and reply-to with the
        // empty values the admin was never shown.
        throw err;
      }
    },
  });

  const deliveriesQuery = useQuery<{ items: MailDeliveryRow[]; total: number }>({
    queryKey: ['admin', 'settings', 'email', 'deliveries'],
    queryFn: () =>
      apiClient.get<{ items: MailDeliveryRow[]; total: number }>(
        '/admin/settings/email/deliveries?limit=20',
      ),
  });

  const setting = settingQuery.data ?? null;

  // Seed the form from the store exactly once, so typing is never clobbered by
  // a background refetch — and NOT AT ALL while the read has failed (R5), so a
  // transient error cannot leave an empty form standing in for a stored
  // configuration.
  if (!loaded && !settingQuery.isLoading && !settingQuery.isError) {
    if (setting) {
      setForm({
        host: setting.host,
        port: String(setting.port),
        secure: setting.secure,
        username: setting.username ?? '',
        password: '',
        fromAddress: setting.fromAddress,
        fromName: setting.fromName ?? '',
        replyTo: setting.replyTo ?? '',
        enabled: setting.enabled,
      });
    }
    setLoaded(true);
  }

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'email'] });
  };

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiClient.put<TenantMailSetting>('/admin/settings/email', payload),
    onSuccess: async () => {
      await invalidate();
      setForm((f) => ({ ...f, password: '' }));
      toast.success('Outgoing mail settings saved');
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Could not save outgoing mail settings'),
  });

  const removeMutation = useMutation({
    // The route accepts an optional `{ reason }` body; `apiClient.delete` takes
    // a RequestInit, so the reason goes on the body rather than as a stray
    // option the fetch wrapper would drop.
    mutationFn: () =>
      apiClient.delete('/admin/settings/email', {
        body: JSON.stringify({ reason: 'Removed from Admin › Settings › Integrations' }),
        headers: { 'content-type': 'application/json' },
      }),
    onSuccess: async () => {
      await invalidate();
      setForm({ ...EMPTY_MAIL_FORM });
      setLastTest(null);
      toast.success('Outgoing mail relay removed — mail falls back to the platform default');
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Could not remove the relay'),
  });

  const testMutation = useMutation({
    mutationFn: () => apiClient.post<MailTestResult>('/admin/settings/email/test', {}),
    onSuccess: async (result) => {
      setLastTest(result);
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'settings', 'email', 'deliveries'],
      });
      if (result.delivery === 'sent') toast.success('Test message sent');
      else if (result.delivery === 'disabled') toast.warning('No mail transport is configured');
      else toast.error(`Test message failed: ${result.errorCode ?? 'unknown error'}`);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Could not send the test message'),
  });

  const port = parsePortInput(form.port);

  const writePayload = (password?: string | null) => ({
    host: form.host.trim(),
    port: port.value,
    secure: form.secure,
    username: form.username.trim() ? form.username.trim() : null,
    // An EMPTY input leaves the stored credential alone (the key is omitted).
    // Clearing it is the "Clear stored password" control, which sends null.
    ...(password === undefined ? {} : { password }),
    fromAddress: form.fromAddress.trim(),
    fromName: form.fromName.trim() ? form.fromName.trim() : null,
    replyTo: form.replyTo.trim() ? form.replyTo.trim() : null,
    enabled: form.enabled,
  });

  // A failed READ disables the WRITE (R5): saving a form that was never seeded
  // from the store is how the stored configuration gets silently replaced.
  const canSave =
    form.host.trim().length > 0 &&
    form.fromAddress.trim().length > 0 &&
    port.value !== null &&
    !settingQuery.isError;

  return (
    <SectionCard
      title="Outgoing mail (SMTP)"
      description="Your own relay for invitations, password resets and notifications. Leave it unconfigured to use the platform default."
      icon={Mail}
      actions={
        settingQuery.isError ? (
          // NOT "Platform default" (R5). A read that failed says nothing about
          // what is configured, and a badge that guesses is the first half of
          // the save that overwrites it.
          <Badge
            variant="outline"
            className="h-5 text-[10px] px-1.5 border-red-500/40 text-red-600 dark:text-red-400"
          >
            Unavailable
          </Badge>
        ) : setting ? (
          <Badge
            variant="outline"
            className={cn(
              'h-5 text-[10px] px-1.5',
              setting.enabled
                ? 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400'
                : 'text-muted-foreground',
            )}
          >
            {setting.enabled ? 'Active' : 'Inactive'}
          </Badge>
        ) : (
          <Badge variant="outline" className="h-5 text-[10px] px-1.5 text-muted-foreground">
            Platform default
          </Badge>
        )
      }
    >
      <div className="space-y-4" data-testid="outgoing-mail-section">
        {settingQuery.isError && (
          <div
            className="rounded-md border border-red-500/40 bg-red-500/5 p-3 text-[11px]"
            data-testid="mail-settings-error"
          >
            <div className="font-medium">Could not load the outgoing mail settings</div>
            <div className="mt-1 text-muted-foreground">
              {settingQuery.error instanceof Error
                ? settingQuery.error.message
                : 'The request failed.'}{' '}
              Saving is disabled until this loads — whatever is configured is still in
              use.
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px] mt-2"
              onClick={() => settingQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2 space-y-1">
            <Label htmlFor="mail-host" className="text-[11px]">SMTP host</Label>
            <Input
              id="mail-host"
              className="h-8 text-xs"
              placeholder="smtp.internal.example"
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="mail-port" className="text-[11px]">Port</Label>
            <Input
              id="mail-port"
              type="number"
              className="h-8 text-xs"
              value={form.port}
              // The RAW string (B2dT3-10). Coercion happens once, in
              // `parsePortInput`, where an empty or half-typed field is a
              // message on the field rather than a `0` or a `null` in the body.
              onChange={(e) => setForm({ ...form, port: e.target.value })}
            />
            {port.error && (
              <div className="text-[10px] text-red-600 dark:text-red-400" data-testid="mail-port-error">
                {port.error}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label htmlFor="mail-secure" className="text-[11px]">Encryption</Label>
            <Select
              value={form.secure}
              onValueChange={(v) => setForm({ ...form, secure: v as TenantMailSetting['secure'] })}
            >
              <SelectTrigger id="mail-secure" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="starttls" className="text-xs">STARTTLS (587)</SelectItem>
                <SelectItem value="tls" className="text-xs">Implicit TLS (465)</SelectItem>
                <SelectItem value="none" className="text-xs">None (internal relay)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="mail-username" className="text-[11px]">Username (optional)</Label>
            <Input
              id="mail-username"
              className="h-8 text-xs"
              placeholder="Blank for an unauthenticated relay"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="mail-password" className="text-[11px]">
              Password {setting?.hasCredentials ? '(stored)' : ''}
            </Label>
            <Input
              id="mail-password"
              type="password"
              className="h-8 text-xs"
              placeholder={setting?.hasCredentials ? 'Blank keeps the stored password' : ''}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label htmlFor="mail-from" className="text-[11px]">From address</Label>
            <Input
              id="mail-from"
              className="h-8 text-xs"
              placeholder="noreply@example.com"
              value={form.fromAddress}
              onChange={(e) => setForm({ ...form, fromAddress: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="mail-from-name" className="text-[11px]">From name</Label>
            <Input
              id="mail-from-name"
              className="h-8 text-xs"
              placeholder="ArgusAI"
              value={form.fromName}
              onChange={(e) => setForm({ ...form, fromName: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="mail-reply-to" className="text-[11px]">Reply-to (optional)</Label>
            <Input
              id="mail-reply-to"
              className="h-8 text-xs"
              value={form.replyTo}
              onChange={(e) => setForm({ ...form, replyTo: e.target.value })}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="flex items-center gap-2">
            <Switch
              id="mail-enabled"
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
            />
            <Label htmlFor="mail-enabled" className="text-[11px]">
              Use this relay (off falls back to the platform default)
            </Label>
          </div>
          <div className="flex items-center gap-2">
            {setting?.hasCredentials && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs"
                onClick={() => saveMutation.mutate(writePayload(null))}
                // Clearing the password is a full PUT like any other, so it is
                // held to the same validity as Save.
                disabled={!canSave || saveMutation.isPending}
              >
                Clear stored password
              </Button>
            )}
            {setting && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs text-red-600 dark:text-red-400"
                onClick={() => removeMutation.mutate()}
                disabled={removeMutation.isPending}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Remove relay
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
            >
              <TestTube2 className="h-3.5 w-3.5 mr-1.5" />
              {testMutation.isPending ? 'Sending…' : 'Send test message'}
            </Button>
            <Button
              size="sm"
              className="h-8 text-xs"
              onClick={() => saveMutation.mutate(writePayload(form.password || undefined))}
              disabled={!canSave || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        {lastTest && (
          <div
            className={cn(
              'rounded-md border p-3 text-[11px]',
              lastTest.delivery === 'sent'
                ? 'border-emerald-500/40 bg-emerald-500/5'
                : lastTest.delivery === 'disabled'
                  ? 'border-amber-500/40 bg-amber-500/5'
                  : 'border-red-500/40 bg-red-500/5',
            )}
            data-testid="mail-test-result"
          >
            <div className="font-medium">
              {lastTest.delivery === 'sent'
                ? `Sent via the ${lastTest.transport} relay`
                : lastTest.delivery === 'disabled'
                  ? 'No mail transport is configured — nothing was sent'
                  : `Failed on the ${lastTest.transport} relay`}
            </div>
            {lastTest.error && (
              <div className="mt-1 font-mono text-muted-foreground break-all">
                {lastTest.errorCode}: {lastTest.error}
              </div>
            )}
            {lastTest.deliveryRow && (
              <div className="mt-1 text-muted-foreground">
                Recorded at {new Date(lastTest.deliveryRow.createdAt).toLocaleString()}
                {lastTest.deliveryRow.messageId ? ` · ${lastTest.deliveryRow.messageId}` : ''}
              </div>
            )}
          </div>
        )}

        {/* The delivery log. Every send leaves a row here, including the ones
            that never opened a socket. */}
        <div className="pt-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium">Recent deliveries</span>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {deliveriesQuery.data?.total ?? 0} recorded
            </span>
          </div>
          {deliveriesQuery.isLoading && (
            <div className="text-[11px] text-muted-foreground mt-2">Loading…</div>
          )}
          {!deliveriesQuery.isLoading && (deliveriesQuery.data?.items.length ?? 0) === 0 && (
            <div className="text-[11px] text-muted-foreground mt-2">
              Nothing sent yet. Invitations, password resets and notifications appear here.
            </div>
          )}
          <div className="mt-2 space-y-1">
            {(deliveriesQuery.data?.items ?? []).map((row) => (
              <div
                key={row.id}
                className="flex items-center gap-3 rounded-md border px-3 py-2 text-[11px]"
                data-testid={`mail-delivery-${row.id}`}
              >
                <StatusDot
                  status={
                    row.outcome === 'sent'
                      ? 'healthy'
                      : row.outcome === 'failed'
                        ? 'failed'
                        : 'disabled'
                  }
                />
                <span className="w-24 shrink-0 capitalize text-muted-foreground">
                  {row.category.replace(/_/g, ' ')}
                </span>
                <span className="flex-1 truncate font-mono">{row.toAddress}</span>
                <span className="w-20 shrink-0 text-muted-foreground">{row.transport}</span>
                <span className="w-40 shrink-0 truncate text-muted-foreground">
                  {row.errorCode ?? row.subject}
                </span>
                <span className="w-36 shrink-0 text-right text-muted-foreground tabular-nums">
                  {new Date(row.createdAt).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Row ────────────────────────────────────────────────────────────────────

function IntegrationRow({
  integration, onToggle, onEdit, onTest, onDelete, onItar,
}: {
  integration: Integration;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
  onTest: () => void;
  onDelete: () => void;
  onItar: () => void;
}) {
  const status: IntegrationStatus = integration.enabled ? integration.status : 'disabled';
  const statusLabel = integration.enabled
    ? (integration.status[0].toUpperCase() + integration.status.slice(1))
    : 'Disabled';
  const textColor = status === 'healthy' ? 'text-emerald-600 dark:text-emerald-400'
    : status === 'degraded' ? 'text-amber-600 dark:text-amber-400'
    : status === 'failed' ? 'text-red-600 dark:text-red-400'
    : 'text-muted-foreground';

  const lastTest = integration.lastTestAt
    ? new Date(integration.lastTestAt).toLocaleString()
    : '—';

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 border rounded-md transition-colors',
        integration.enabled ? 'bg-card' : 'bg-muted/30',
      )}
      data-testid={`integration-row-${integration.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium truncate">{integration.name}</span>
          {integration.provider && (
            <Badge variant="outline" className="h-4 text-[9px] px-1.5">
              {integration.provider}
            </Badge>
          )}
          <StatusDot status={status} />
          <span className={cn('text-[10px]', textColor)}>{statusLabel}</span>
          {integration.itarCleared && (
            <Badge variant="outline" className="h-4 text-[9px] px-1.5 gap-1 border-amber-500/40 text-amber-700 dark:text-amber-400">
              <ShieldCheck className="h-2.5 w-2.5" />
              ITAR cleared
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
          <span className="font-mono truncate">{integration.endpoint ?? '—'}</span>
          <span className="shrink-0">•</span>
          <span className="shrink-0 flex items-center gap-1">
            <Clock className="h-2.5 w-2.5" />
            Tested {lastTest}
          </span>
          {integration.lastTestResult?.errorCode && (
            <>
              <span className="shrink-0">•</span>
              <span className="shrink-0 text-red-600 dark:text-red-400">
                {integration.lastTestResult.errorCode}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1 px-2"
          title="Send a probe to verify connectivity"
          onClick={onTest}
        >
          <TestTube2 className="h-3 w-3" />
          Test
        </Button>
        <Switch
          checked={integration.enabled}
          onCheckedChange={onToggle}
          className="scale-75"
          aria-label={integration.enabled ? 'Disable integration' : 'Enable integration'}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="More actions">
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={onEdit} className="text-xs gap-2">
              <Pencil className="h-3 w-3" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onTest} className="text-xs gap-2">
              <TestTube2 className="h-3 w-3" />
              Fire test probe
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onItar} className="text-xs gap-2">
              <ShieldCheck className="h-3 w-3" />
              {integration.itarCleared ? 'Revoke ITAR clearance' : 'Mark ITAR-cleared'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onDelete}
              className="text-xs gap-2 text-red-600 focus:text-red-600"
            >
              <Trash2 className="h-3 w-3" />
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// ─── Stat card ──────────────────────────────────────────────────────────────

function StatCard({
  label, value, accent, icon: Icon,
}: {
  label: string;
  value: number;
  accent: 'emerald' | 'amber' | 'red' | 'slate';
  icon: React.ComponentType<{ className?: string }>;
}) {
  const border = accent === 'emerald' ? 'border-l-emerald-500'
    : accent === 'amber' ? 'border-l-amber-500'
    : accent === 'red' ? 'border-l-red-500'
    : 'border-l-slate-400';
  const iconColor = accent === 'emerald' ? 'text-emerald-500'
    : accent === 'amber' ? 'text-amber-500'
    : accent === 'red' ? 'text-red-500'
    : 'text-slate-500';
  return (
    <Card className={cn('border-l-4', border)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
            <div className="text-xl font-semibold tabular-nums mt-0.5">{value}</div>
          </div>
          <Icon className={cn('h-4 w-4', iconColor)} />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Test-fire dialog ───────────────────────────────────────────────────────

function TestFireDialog({
  integration, open, onClose,
}: {
  integration: Integration;
  open: boolean;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<'confirm' | 'running' | 'result'>('confirm');
  const [result, setResult] = useState<{
    success: boolean;
    latencyMs: number;
    errorMessage: string | null;
    errorCode: string | null;
    status: IntegrationStatus;
  } | null>(null);

  const fireMutation = useMutation({
    mutationFn: () =>
      apiClient.post<{
        success: boolean;
        latencyMs: number;
        errorMessage: string | null;
        errorCode: string | null;
        status: IntegrationStatus;
        testedAt: string;
      }>(`/admin/settings/integrations/${integration.id}/test`, {}),
    onSuccess: async (data) => {
      setResult(data);
      setPhase('result');
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'settings', 'integrations'],
      });
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Probe failed';
      setResult({
        success: false,
        latencyMs: 0,
        errorMessage: m,
        errorCode: 'CLIENT_ERROR',
        status: 'failed',
      });
      setPhase('result');
    },
  });

  function fire() {
    setPhase('running');
    fireMutation.mutate();
  }

  function close() {
    setPhase('confirm');
    setResult(null);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <TestTube2 className="h-4 w-4" />
            Test {integration.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {TYPE_META[integration.type].label}
          </DialogDescription>
        </DialogHeader>

        {phase === 'confirm' && (
          <div className="space-y-3 text-xs">
            <div className="p-3 rounded bg-muted/40 text-muted-foreground">
              Sends a real network probe (5s timeout) to the configured endpoint.
              Air-gap-isolated tenants will see ECONNREFUSED / DNS failure here —
              that result is expected and will be recorded for audit.
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <Label className="text-muted-foreground uppercase tracking-wide">Endpoint:</Label>
              <span className="font-mono">{integration.endpoint ?? '(none)'}</span>
            </div>
          </div>
        )}

        {phase === 'running' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <div className="text-xs text-muted-foreground">Firing probe…</div>
          </div>
        )}

        {phase === 'result' && result && (
          <div className={cn(
            'p-3 rounded border text-xs',
            result.success
              ? 'bg-emerald-500/5 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
              : 'bg-red-500/5 border-red-500/30 text-red-700 dark:text-red-300',
          )}>
            <div className="flex items-center gap-2 font-medium mb-1">
              {result.success ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
              {result.success ? 'Probe succeeded' : 'Probe failed'}
              {result.errorCode && <Badge variant="outline" className="ml-auto h-4 text-[9px]">{result.errorCode}</Badge>}
            </div>
            {result.errorMessage && <div className="pl-5">{result.errorMessage}</div>}
            <div className="pl-5 mt-2 text-[10px] opacity-80 tabular-nums">
              Latency: {result.latencyMs}ms · Status: {result.status}
            </div>
          </div>
        )}

        <DialogFooter>
          {phase === 'confirm' && (
            <>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={close}>
                Cancel
              </Button>
              <Button size="sm" className="h-8 text-xs gap-1.5" onClick={fire}>
                <TestTube2 className="h-3 w-3" />
                Fire probe
              </Button>
            </>
          )}
          {phase === 'result' && (
            <Button size="sm" className="h-8 text-xs" onClick={close}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Editor sheet ───────────────────────────────────────────────────────────

function IntegrationEditorSheet({
  integration, open, onClose,
}: {
  integration: Integration;
  open: boolean;
  onClose: () => void;
}) {
  const [name, setName] = useState(integration.name);
  const [provider, setProvider] = useState(integration.provider ?? '');
  const [endpoint, setEndpoint] = useState(integration.endpoint ?? '');
  const [reason, setReason] = useState('');
  const [rotateCredentials, setRotateCredentials] = useState(false);
  const [credentialsRaw, setCredentialsRaw] = useState('');

  const saveMutation = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        name,
        provider: provider || null,
        endpoint: endpoint || null,
        reason: reason || undefined,
      };
      if (rotateCredentials && credentialsRaw.trim()) {
        // Parse newline=KEY:VALUE pairs into a credentials map
        const creds: Record<string, string> = {};
        for (const line of credentialsRaw.split('\n')) {
          const m = line.match(/^\s*([A-Za-z0-9_]+)\s*[:=]\s*(.+?)\s*$/);
          if (m) creds[m[1]] = m[2];
        }
        body.credentials = creds;
      }
      return apiClient.patch(`/admin/settings/integrations/${integration.id}`, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'settings', 'integrations'],
      });
      toast.success('Integration updated');
      onClose();
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Save failed';
      toast.error(`Save failed: ${m}`);
    },
  });

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-lg p-0 flex flex-col">
        <SheetHeader className="p-5 border-b shrink-0">
          <SheetTitle className="text-base flex items-center gap-2">
            <Pencil className="h-4 w-4" />
            Edit {integration.name}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {TYPE_META[integration.type].description}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto min-h-0 p-5 space-y-5">
          {/* Identity */}
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-8 text-xs mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Provider</Label>
              <Input
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="h-8 text-xs mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Endpoint</Label>
              <Input
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                className="h-8 text-xs mt-1 font-mono"
              />
            </div>
          </div>

          <Separator />

          {/* Credentials */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Credentials</Label>
              <div className="flex items-center gap-2">
                <Switch
                  checked={rotateCredentials}
                  onCheckedChange={setRotateCredentials}
                  className="scale-75"
                />
                <span className="text-[10px] text-muted-foreground">
                  Rotate credentials
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2.5 border rounded bg-muted/30">
              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
              <code className="text-[10px] font-mono truncate">
                {integration.credentialsVaultRef ?? '(none)'}
              </code>
            </div>
            {rotateCredentials && (
              <Textarea
                value={credentialsRaw}
                onChange={(e) => setCredentialsRaw(e.target.value)}
                rows={4}
                placeholder="HEC_TOKEN: xxxxx&#10;API_KEY: yyyyy"
                className="text-[10px] font-mono"
              />
            )}
            <p className="text-[10px] text-muted-foreground">
              Secrets are stored in the SOPS+age vault. Plaintext is never
              displayed or returned by the API.
            </p>
          </div>

          <Separator />

          {/* Reason */}
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Change reason (audit)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Optional reason — recorded in audit_logs"
              className="h-8 text-xs mt-1"
            />
          </div>

          {/* Type config (read-only — type is immutable) */}
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Configuration (read-only)</Label>
            <pre className="mt-1 p-2 rounded bg-muted/30 text-[10px] font-mono overflow-auto max-h-48">
              {JSON.stringify(integration.config, null, 2)}
            </pre>
            <p className="text-[10px] text-muted-foreground mt-1">
              Integration type is immutable post-create. To change config,
              delete and recreate.
            </p>
          </div>
        </div>

        <div className="border-t shrink-0 p-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Create dialog ─────────────────────────────────────────────────────────

function CreateIntegrationDialog({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [type, setType] = useState<IntegrationType>('siem');
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [configRaw, setConfigRaw] = useState('');
  const [reason, setReason] = useState('');

  const createMutation = useMutation({
    mutationFn: () => {
      const config = buildDefaultConfig(type, endpoint, configRaw);
      const body = {
        name,
        provider: provider || null,
        endpoint: endpoint || null,
        config,
        enabled: false,
        reason: reason || undefined,
      };
      return apiClient.post('/admin/settings/integrations', body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'settings', 'integrations'],
      });
      toast.success('Integration created — run a test probe before enabling');
      onClose();
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Create failed';
      toast.error(`Create failed: ${m}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add integration
          </DialogTitle>
          <DialogDescription className="text-xs">
            New integrations are created disabled. Run a test probe before enabling.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Type</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {(Object.keys(TYPE_META) as IntegrationType[]).map(t => {
                const TypeIcon = TYPE_ICON[t];
                const isActive = type === t;
                return (
                  <button
                    key={t}
                    onClick={() => setType(t)}
                    className={cn(
                      'flex items-center gap-2 p-2 border rounded text-[11px] transition-colors',
                      isActive ? 'border-primary bg-primary/5' : 'hover:bg-muted/30',
                    )}
                  >
                    <span className={cn('h-6 w-6 rounded flex items-center justify-center', TYPE_COLOR[t])}>
                      <TypeIcon className="h-3 w-3" />
                    </span>
                    <span className="truncate text-left">{TYPE_META[t].label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`e.g. ${TYPE_META[type].label} - production`}
              className="h-8 text-xs mt-1"
            />
          </div>

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Provider</Label>
            <Input
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              placeholder={defaultProviderForType(type)}
              className="h-8 text-xs mt-1"
            />
          </div>

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Endpoint</Label>
            <Input
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder={defaultEndpointForType(type)}
              className="h-8 text-xs mt-1 font-mono"
            />
          </div>

          {(type === 'object_storage' || type === 's3_object_lock' || type === 'azure_immutable_blob') && (
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                {type === 'azure_immutable_blob' ? 'Container / Storage account' : 'Bucket / Region'}
              </Label>
              <Input
                value={configRaw}
                onChange={(e) => setConfigRaw(e.target.value)}
                placeholder={
                  type === 'azure_immutable_blob'
                    ? 'mycontainer/mystorageaccount'
                    : 'mybucket/us-central'
                }
                className="h-8 text-xs mt-1 font-mono"
              />
            </div>
          )}

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Reason (audit)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Optional"
              className="h-8 text-xs mt-1"
            />
          </div>

          <div className="p-2.5 bg-muted/40 rounded text-[10px] text-muted-foreground flex items-start gap-2">
            <Shield className="h-3 w-3 mt-0.5 shrink-0" />
            <span>
              Credentials are configured separately via the secret vault.
              Type-specific advanced settings can be edited from the row menu.
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => {
              if (!name.trim()) {
                toast.error('Name is required');
                return;
              }
              createMutation.mutate();
            }}
            disabled={createMutation.isPending}
          >
            <Plus className="h-3 w-3" />
            {createMutation.isPending ? 'Creating…' : 'Create integration'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── ITAR-clear dialog ─────────────────────────────────────────────────────

function ItarClearDialog({
  integration, open, onClose,
}: {
  integration: Integration;
  open: boolean;
  onClose: () => void;
}) {
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [reason, setReason] = useState('');

  const next = !integration.itarCleared;

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/admin/settings/integrations/${integration.id}/itar-clear`, {
        itarCleared: next,
        confirmPhrase,
        reason,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'settings', 'integrations'],
      });
      toast.success(next ? 'Marked ITAR-cleared (audit-chain)' : 'ITAR clearance revoked');
      onClose();
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Action failed';
      toast.error(m);
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-amber-600" />
            {next ? 'Mark ITAR-cleared' : 'Revoke ITAR clearance'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            This event is hash-chained to the audit ledger and requires
            security.privileged. Type the integration name to confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Integration</Label>
            <div className="text-xs font-mono mt-1">{integration.name}</div>
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Type the name to confirm</Label>
            <Input
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              placeholder={integration.name}
              className="h-8 text-xs mt-1 font-mono"
            />
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Reason (required)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Vendor signed ITAR addendum #4521 dated 2026-04-25"
              rows={3}
              className="text-xs mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            className={cn(
              'h-8 text-xs',
              next ? 'bg-amber-600 hover:bg-amber-700' : ''
            )}
            disabled={
              confirmPhrase !== integration.name ||
              reason.length < 5 ||
              mutation.isPending
            }
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Submitting…' : next ? 'Mark ITAR-cleared' : 'Revoke clearance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildDefaultConfig(
  type: IntegrationType,
  endpoint: string,
  raw: string
): Record<string, unknown> {
  switch (type) {
    case 'siem':
      return {
        type: 'siem',
        endpoint: endpoint || 'https://splunk.example.local:8088',
        source_type: 'splunk_hec',
      };
    case 'chat_ops':
      return {
        type: 'chat_ops',
        workspace_id: 'workspace',
        default_channel: '#argusai',
        oauth_scope: 'chat:write,channels:read',
        provider: 'slack',
      };
    case 'scim':
      return {
        type: 'scim',
        endpoint: endpoint || 'https://argusai.example.local/scim/v2',
        version: 'v2',
        authentication_method: 'bearer',
      };
    case 'object_storage': {
      const [bucket, region] = (raw || 'argusai-prod/us-central').split('/');
      return {
        type: 'object_storage',
        bucket: bucket || 'argusai-prod',
        region: region || 'us-central',
        provider: 's3',
        endpoint: endpoint || null,
      };
    }
    case 'calendar':
      return {
        type: 'calendar',
        provider: 'google',
        default_calendar_id: 'primary',
      };
    case 's3_object_lock': {
      const [bucket, region] = (raw || 'argusai-audit/us-central').split('/');
      return {
        type: 's3_object_lock',
        bucket: bucket || 'argusai-audit',
        region: region || 'us-central',
        retention_mode: 'compliance',
        retention_days: 1825,
      };
    }
    case 'azure_immutable_blob': {
      const [container, account] = (raw || 'argusaiwitness/argusaistorage').split('/');
      return {
        type: 'azure_immutable_blob',
        container: container || 'argusaiwitness',
        storage_account: account || 'argusaistorage',
        immutability_period_days: 1825,
      };
    }
  }
}

function defaultProviderForType(type: IntegrationType): string {
  switch (type) {
    case 'siem': return 'Splunk HEC';
    case 'chat_ops': return 'Slack';
    case 'scim': return 'Okta';
    case 'object_storage': return 'MinIO';
    case 'calendar': return 'Google';
    case 's3_object_lock': return 'AWS S3';
    case 'azure_immutable_blob': return 'Azure Blob';
  }
}

function defaultEndpointForType(type: IntegrationType): string {
  switch (type) {
    case 'siem': return 'https://splunk.example.local:8088';
    case 'chat_ops': return 'https://chat.example.local';
    case 'scim': return 'https://argusai.example.local/scim/v2';
    case 'object_storage': return 'https://s3.example.local:9000';
    case 'calendar': return 'https://www.googleapis.com/calendar/v3';
    case 's3_object_lock': return 'https://s3.us-east-1.amazonaws.com';
    case 'azure_immutable_blob': return 'https://argusaistorage.blob.core.windows.net';
  }
}
